import React, { useEffect } from "react";
import * as Icons from "@wix/wix-ui-icons-common";
import {
  Box,
  Button,
  Card,
  WixDesignSystemProvider,
  MarketingPageLayout,
  MarketingPageLayoutContent,
  Page,
  Loader,
  CustomModalLayout,
  Modal,
  Notification,
  Cell,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { ReactTagManager } from "react-gtm-ts";
import { useTranslation } from "react-i18next";
import { identify, track, WIX_APP_ID } from "./analytics";

const tagManagerArgs = {
  code: "GTM-WZQPMC7",
};

const BASE_URL = "https://wix.certifiedcode.io/spotify/_functions";

function App() {
  const { t } = useTranslation();
  const token = new URLSearchParams(window.location.search).get("token");
  const instance = new URLSearchParams(window.location.search).get("instance");
  const [isUpgraded, setIsUpgraded] = React.useState(false);
  const [instanceData, setInstanceData] = React.useState({
    instance: {
      isFree: true,
      availablePlans: [],
      instanceId: "",
    },
  } as any);
  const [InteractiveDemoOpen, setInteractiveDemoOpen] = React.useState(false);
  const isFree = instanceData?.instance?.isFree !== false;

  const [isUpgradeBannerOpen, setIsUpgradeBannerOpen] =
    React.useState<boolean>(isFree);

  useEffect(() => {
    ReactTagManager.init(tagManagerArgs);
  }, []);

  useEffect(() => {
    identify(instance);
    track("dashboard_opened", { has_token: Boolean(token) });
    setInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, token]);

  function setInstance() {
    fetch(BASE_URL + "/@certifiedcode/base-backend/instance", {
      method: "GET",
      headers: {
        Authorization: instance || "",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setInstanceData(data);
        const upgraded = data?.instance?.isFree === false;
        setIsUpgraded(upgraded);
        setIsUpgradeBannerOpen(!upgraded);
      })
      .catch(() => {});
  }

  if (token) {
    track("install_redirect");
    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }}>
        <Page height="100vh">
          <Page.Content>
            <Box height={"90vh"} direction="vertical" verticalAlign="middle">
              <Loader text={t("loading")} />
            </Box>
          </Page.Content>
        </Page>
      </WixDesignSystemProvider>
    );
  }

  const upgradeUrl = `https://memberships.certifiedcode.us/${window.location.search}`;
  const openInteractiveDemo = () => {
    track("watch_video_clicked");
    setInteractiveDemoOpen(true);
  };

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <Modal
        isOpen={InteractiveDemoOpen}
        screen="desktop"
        shouldDisplayCloseButton={true}
        shouldCloseOnOverlayClick={true}
        onRequestClose={() => setInteractiveDemoOpen(false)}
      >
        <CustomModalLayout
          removeContentPadding={true}
          content={
            <div style={{ position: "relative", paddingBottom: "calc(56.22% + 41px)", height: 0, width: "100%" }}>
              <iframe
                src="https://app.arcade.software/share/yhiSDl2NqBwv3kwZPF2O?embed&embed_mobile=inline&embed_desktop=inline&show_copy_link=true"
                title="Get Paid with Spotify Music Player | Getting Started"
                frameBorder="0"
                loading="lazy"
                allow="clipboard-write"
                allowFullScreen
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", colorScheme: "light" }}
              ></iframe>
            </div>
          }
        />
      </Modal>
      <Notification theme="premium" show={isUpgradeBannerOpen} type={"sticky"}>
        <Notification.TextLabel>{t("upgrade_banner")}</Notification.TextLabel>
        <Notification.ActionButton
          type="textLink"
          link={upgradeUrl}
          target="_blank"
          onClick={() => {
            track("upgrade_clicked", { source: "banner" });
          }}
        >
          {t("upgrade")}
        </Notification.ActionButton>
        <Notification.CloseButton
          onClick={() => setIsUpgradeBannerOpen(false)}
        />
      </Notification>
      <Card>
        <MarketingPageLayout
          removeImageHorizontalPadding
          removeImageVerticalPadding
          content={
            <Box height="440px" verticalAlign="middle">
              <MarketingPageLayoutContent
                title={t("title")}
                content={t("content")}
                actions={
                  <Box direction="vertical" gap="SP2">
                    <Button
                      skin="premium"
                      prefixIcon={<Icons.PremiumFilled />}
                      as="a"
                      href={upgradeUrl}
                      target="_blank"
                      onClick={() => track("upgrade_clicked", { source: "cta" })}
                    >
                      {isUpgraded ? t("manage_plan") : t("upgrade_app")}
                    </Button>
                    <Button
                      priority="secondary"
                      prefixIcon={<Icons.GetStarted />}
                      onClick={openInteractiveDemo}
                    >
                      {t("setup_guide")}
                    </Button>
                    <Button
                      skin="inverted"
                      prefixIcon={<Icons.Reviews />}
                      as="a"
                      href={`https://bridget.reviews.certifiedcode.us/${WIX_APP_ID}${window.location.search}`}
                      target="_blank"
                      onClick={() => track("write_review_clicked")}
                    >
                      {t("write_review")}
                    </Button>
                  </Box>
                }
              />
            </Box>
          }
        />
      </Card>
      <Cell span={12}>
        <Card>
          <MarketingPageLayout
            removeImageHorizontalPadding
            removeImageVerticalPadding
            content={
              <Box marginTop="-30px" verticalAlign="middle">
                <MarketingPageLayoutContent
                  size="medium"
                  title={t("setup_title")}
                  content={t("setup_content")}
                  actions={
                    <Box marginTop="-30px">
                      <Button
                        size="small"
                        skin="premium"
                        prefixIcon={<Icons.PlayFilled />}
                        onClick={openInteractiveDemo}
                      >
                        {t("watch_video")}
                      </Button>
                    </Box>
                  }
                />
              </Box>
            }
          />
        </Card>
      </Cell>
    </WixDesignSystemProvider>
  );
}

export default App;
